/**
 * Usage telemetry — per-tenant product analytics for the platform console
 * (docs/FEATURE_PACKS_PLAN.md).
 *
 * WHAT IT RECORDS
 *   - page views, keyed by a normalised route (IDs stripped)
 *   - which users were active each day
 *   - named product events via logUsageEvent() (sparingly — counters, not
 *     an event stream)
 *
 * HOW IT'S STORED — one Firestore doc per tenant per LOCAL day:
 *   usage_daily/{tenantId}_{yyyy-mm-dd}
 *     { tenantId, date, pageViews: {routeKey: n}, events: {key: n},
 *       activeUsers: {userId: true}, updatedAt }
 *
 * Views buffer in memory and flush as ONE merge write with increment
 * sentinels (every 15s when dirty, immediately at 25 buffered views, and
 * on tab-hide). A day of clicking is a handful of writes, not thousands.
 *
 * PRIVACY / SECURITY SHAPE: counters only — no URLs with IDs, no customer
 * data, no payloads. Clients can WRITE their own tenant's doc but nobody
 * can READ the collection client-side (deny-read in firestore.rules);
 * the platform console reads it server-side via the Admin SDK.
 *
 * Telemetry must never break the app: every path is wrapped, failures are
 * swallowed after a console.debug.
 */
import { doc, increment, setDoc } from 'firebase/firestore';
import { db } from '@/firebase';
import { getTenantIdSync } from '@/lib/tenant';
import { getSession } from '@/lib/session';

const FLUSH_INTERVAL_MS = 15_000;
const FLUSH_AT_BUFFERED = 25;

/** Strip volatile segments so routes aggregate: doc IDs, tokens, numbers. */
export function normalizeRouteKey(pathname: string): string {
  const segments = pathname.split('?')[0].split('#')[0].split('/').filter(Boolean);
  const cleaned = segments.map((seg) => {
    // Firestore push-ID / UUID / token / numeric segments → ':id'
    if (/^\d+$/.test(seg)) return ':id';
    if (seg.length >= 16) return ':id';
    if (/^[0-9a-f]{8}-[0-9a-f]{4}/i.test(seg)) return ':id';
    return seg;
  });
  // Collapse trailing ':id' repeats (e.g. /p/:id/:id)
  const key = cleaned.slice(0, 4).join('_') || 'root';
  // Firestore field names: keep to safe chars.
  return key.replace(/[^a-zA-Z0-9_:-]/g, '_');
}

/** Local-time YYYY-MM-DD (never toISOString — AEST, see CLAUDE.md). */
function localDate(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

interface Buffer {
  pageViews: Map<string, number>;
  events: Map<string, number>;
  buffered: number;
}

const buf: Buffer = { pageViews: new Map(), events: new Map(), buffered: 0 };
let flushTimer: ReturnType<typeof setInterval> | null = null;
let listenersBound = false;

function currentUserId(): string | null {
  try {
    const session = getSession();
    return session.userId || session.techId;
  } catch {
    return null;
  }
}

async function flush(): Promise<void> {
  if (buf.buffered === 0) return;
  const pageViews = Object.fromEntries(
    Array.from(buf.pageViews.entries()).map(([k, n]) => [k, increment(n)]),
  );
  const events = Object.fromEntries(
    Array.from(buf.events.entries()).map(([k, n]) => [k, increment(n)]),
  );
  buf.pageViews.clear();
  buf.events.clear();
  buf.buffered = 0;

  try {
    const tenantId = getTenantIdSync();
    const date = localDate();
    const userId = currentUserId();
    const payload: Record<string, unknown> = {
      tenantId,
      date,
      updatedAt: new Date().toISOString(),
    };
    if (Object.keys(pageViews).length) payload.pageViews = pageViews;
    if (Object.keys(events).length) payload.events = events;
    if (userId) payload.activeUsers = { [userId]: true };
    await setDoc(doc(db, 'usage_daily', `${tenantId}_${date}`), payload, { merge: true });
  } catch (err) {
    // Telemetry never surfaces errors — but leave a trace for dev tools.
    console.debug('[usage-telemetry] flush skipped:', err);
  }
}

function ensurePump(): void {
  if (!flushTimer) {
    flushTimer = setInterval(() => void flush(), FLUSH_INTERVAL_MS);
  }
  if (!listenersBound && typeof document !== 'undefined') {
    listenersBound = true;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') void flush();
    });
  }
}

/** Record one page view. Called by usePageTelemetry on route change. */
export function trackPageView(pathname: string): void {
  try {
    const key = normalizeRouteKey(pathname);
    buf.pageViews.set(key, (buf.pageViews.get(key) ?? 0) + 1);
    buf.buffered += 1;
    ensurePump();
    if (buf.buffered >= FLUSH_AT_BUFFERED) void flush();
  } catch {
    /* never throw from telemetry */
  }
}

/**
 * Record a named product event (counter, not payload). Use sparingly for
 * signals the platform owner cares about: 'job_completed', 'deal_created',
 * 'invoice_sent', 'proposal_sent'...
 */
export function logUsageEvent(key: string): void {
  try {
    const safe = key.replace(/[^a-zA-Z0-9_:-]/g, '_').slice(0, 40);
    buf.events.set(safe, (buf.events.get(safe) ?? 0) + 1);
    buf.buffered += 1;
    ensurePump();
    if (buf.buffered >= FLUSH_AT_BUFFERED) void flush();
  } catch {
    /* never throw from telemetry */
  }
}
